import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ImportRecipeFormList from './ImportRecipeFormList';
import type { RecipeFormData } from '@/types/recipe';
import { addRecipe } from '@/lib/supabase-service';
import { RecipeSource } from '@/types/recipe';
import { useNavigate } from 'react-router-dom';
import { friendlyError } from '@/utils/errorUtils';

interface ImportStepPreviewProps {
  recipes: RecipeFormData[];
  rawLLMOutput: string | null;
  goBack: () => void;
}

export default function ImportStepPreview({ recipes, rawLLMOutput, goBack }: ImportStepPreviewProps) {
  const [selected, setSelected] = useState(() => recipes.map(() => true));
  const [editedRecipes, setEditedRecipes] = useState<RecipeFormData[]>(recipes);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleToggle = (idx: number) => {
    setSelected(sel => sel.map((v, i) => (i === idx ? !v : v)));
  };
  
  const handleSelectAll = () => {
    setSelected(recipes.map(() => true));
  };
  
  const handleDeselectAll = () => {
    setSelected(recipes.map(() => false));
  };
  
  const handleRecipeChange = (idx: number, updated: RecipeFormData) => {
    setEditedRecipes(arr => arr.map((r, i) => (i === idx ? updated : r)));
  };
  const selectedCount = selected.filter(Boolean).length;

  const handleImport = async () => {
    setLoading(true);
    setError(null);
    try {
      const toImport = editedRecipes.filter((_, i) => selected[i]);
      if (toImport.length === 0) return;
      for (const recipe of toImport) {
        await addRecipe({ ...recipe, source: RecipeSource.Imported });
      }
      setImportedCount(toImport.length);
      setShowSuccessModal(true);
    } catch (e: any) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/'); // Navigate back to main page
  };

  return (
    <>
      <div className="space-y-6 max-w-2xl mx-auto py-8">
        <h2 className="text-xl font-bold mb-2">Preview & Edit Recipes</h2>
        
        {/* Recipe count and bulk actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-gray-700 font-semibold">
            Found {recipes.length} recipe{recipes.length !== 1 ? 's' : ''} ready to import.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </div>
        </div>
        
        <ImportRecipeFormList
          recipes={editedRecipes}
          onChange={handleRecipeChange}
          selected={selected}
          onToggle={handleToggle}
        />
        
        <div className="flex flex-col gap-2 mt-8">
          <div className="flex gap-4 justify-between">
            <Button variant="outline" onClick={goBack} disabled={loading}>Back</Button>
            <Button onClick={handleImport} disabled={selectedCount === 0 || loading}>
              {loading ? 'Importing...' : `Import${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
            </Button>
          </div>
          {error && <div className="text-red-600 text-sm font-medium p-3 bg-red-50 border border-red-200 rounded">{error}</div>}
        </div>
        
        {rawLLMOutput && (
          <details className="mt-4">
            <summary className="cursor-pointer text-blue-600">Show raw AI output</summary>
            <pre className="mt-2 p-2 bg-gray-100 text-xs max-h-40 overflow-auto border rounded">
              {rawLLMOutput}
            </pre>
          </details>
        )}
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md bg-blue-50 dark:bg-blue-900/90 border-blue-200 dark:border-blue-700 shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-center text-blue-900 dark:text-blue-100 text-2xl font-bold">Import Successful! 🎉</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="text-lg mb-4 text-blue-900 dark:text-blue-100">
              Successfully imported <strong>{importedCount}</strong> recipe{importedCount !== 1 ? 's' : ''}!
            </div>
            <div className="text-sm text-blue-800 dark:text-blue-200 mb-6">
              Your recipes have been added to your collection and are ready to use.
            </div>
            <Button onClick={handleSuccessModalClose} className="w-full bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold py-2 dark:bg-blue-700 dark:hover:bg-blue-800">
              Go to My Recipes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
